import api, { ApiResponse } from './api';
import { XACCEL_TAB_REQUEST_TIMEOUT_MS, type ServerReadEntry } from '@/utils/server-reads';
import type { LegReal, LegVerdict } from '@/utils/leg-state';

/** A scoped leg action may restart with fresh DRM keys or create several streams (30 s client timeouts per call). */
const LEG_ACTION_REQUEST_TIMEOUT_MS = 180_000;

export interface LinearChannel {
  id: number;
  user_id: number;
  dazn_asset_id: string;
  /** Identity the channel's streams were started with (server-side, read-only). */
  playback_identity?: 'web' | 'androidtv' | null;
  name: string;
  subtitle: string | null;
  image_url: string | null;
  status: 'stopped' | 'running' | 'error';
  status_message: string | null;
  xaccel_channel_name: string | null;
  xaccel_stream_url: string | null;
  xaccel_server_id: number | null;
  server_ids?: string | null;
  xaccel_user_agent: string | null;
  xaccel_stream_enable: number;
  cdn_blacklist: string | null;
  cdn_preferred: string | null;
  linear_manifest: string | null;
  dazn_cdn_token_value: string | null;
  verify_age: number;
  pin_protect: number;
  auto_restart_enabled: number;
  restart_times: string | null;
  last_restart_at: string | null;
  resolution: string;
  video_map_overrides: string | null;
  proxy_by_resolution: string | null;
  max_restart_attempts: number;
  restart_cooldown_minutes: number;
  health_restart_count: number;
  last_health_restart_at: string | null;
  created_at: string;
  updated_at: string;
  // Joined fields
  xaccel_name?: string;
  xaccel_url?: string;
  resolutions?: number[];
  xaccel_channels_count?: number;
  // Server type (xaccel or ffstream)
  server_type?: string;
  // Real streaming server status (enriched by backend)
  real_status?: string;
  mismatch?: boolean;
  // Per-server status (enriched by backend for multi-server)
  servers?: Array<{
    id: number;
    name: string;
    server_type: string;
    running: boolean;
    channels_total: number;
    channels_running: number;
    /** Legs on this server that nothing in the background starts (stopped by the user / no longer selected). */
    channels_held?: number;
    /** true = the server did not answer: the counts come from the DB. */
    unchecked?: boolean;
  }>;
  /** Held legs of the channel (list badge): stopped by the user, or no longer selected. */
  held_legs?: Array<{ name: string; serverId: number; reason: 'user-stop' | 'unwanted' }>;
  /** DB status 'error' while a leg really runs (display only; the self-heal fixes the status). */
  live_while_error?: boolean;
}

export interface WizardChannel {
  id: string;
  title: string;
  subtitle: string | null;
  image: string | null;
  is_live: boolean;
  already_imported: boolean;
}

export interface LinearXaccelChannel {
  id: number;
  linear_channel_id: number;
  resolution_id: number;
  channel_name: string;
  xaccel_server_id: number;
  status: string;
  stream_url: string | null;
  resolution_name: string;
  xaccel_value: string;
  effective_video_map?: string;
  real_status?: string;
  clients?: number;
  uptime?: number;
  mismatch?: boolean;
}

/** Legs on servers no longer selected for the channel: kept running until an explicit delete. */
export interface OrphanedLegs {
  serverId: number;
  serverName: string;
  count: number;
}

/** One leg delete outcome (the leg stays in the list when ok is false). */
export interface LegDeleteResult {
  legId: number;
  serverId: number;
  server: string;
  resolution: string;
  channelName: string;
  ok: boolean;
  alreadyGone?: boolean;
  error?: string;
}

export interface CreateLinearChannelRequest {
  dazn_asset_id: string;
  name: string;
  xaccel_channel_name?: string;
  xaccel_server_id?: number;
  server_ids?: number[];
  auto_restart_enabled?: boolean;
  restart_times?: string;
  resolution?: string;
  max_restart_attempts?: number;
  restart_cooldown_minutes?: number;
}

export interface UpdateLinearChannelRequest {
  name?: string;
  xaccel_channel_name?: string;
  xaccel_server_id?: number;
  server_ids?: number[];
  auto_restart_enabled?: boolean;
  restart_times?: string;
  resolution?: string;
  xaccel_user_agent?: string | null;
  xaccel_stream_enable?: boolean;
  cdn_blacklist?: string;
  cdn_preferred?: string;
  verify_age?: boolean;
  pin_protect?: boolean;
  video_map_overrides?: string | null;
  max_restart_attempts?: number;
  restart_cooldown_minutes?: number;
}

export interface BulkConfigureRequest {
  channels: Array<{
    dazn_asset_id: string;
    name: string;
    subtitle?: string;
    image_url?: string;
  }>;
  xaccel_server_id: number;
  server_ids?: number[];
  resolution_ids: number[];
  prefix?: string;
  separator?: '_' | ' ' | '-';
  proxy_by_resolution?: Record<string, any>;
  auto_restart_enabled?: boolean;
  restart_times?: string[];
  create_xaccel_channels?: boolean;
  /** Required by the server on the real run that creates streams (the user clicked "Esegui" after the preview). */
  confirm?: boolean;
}

/** Wizard preview: the real state per selected server of the legs the new channels would get. */
export interface BulkPlanServer {
  serverId: number;
  serverName: string;
  type: string;
  reachable: boolean;
  error?: string;
  ageMs: number | null;
  legs: Array<{ channel: string; resolution: string; name: string; verdict: 'create' | 'exists-on-server' | 'conflict' | 'unknown' }>;
}

/** Per-server outcome of creating the legs of several channels (wizard, retry). */
export interface BulkServerResult {
  serverId: number;
  serverName: string;
  created: number;
  skipped: Array<{ channelId: number; channel: string; name: string; reason: string }>;
  failed: Array<{ channelId: number; channel: string; name: string; error: string }>;
}

export interface BulkConfigureResponse {
  success: boolean;
  imported: number;
  xaccel_created: number;
  xaccel_failed: number;
  channels: Array<{
    id: number;
    name: string;
    xaccel_channel_name: string;
    xaccel_channels_count: number;
  }>;
  results?: BulkServerResult[];
}

/** One row of GET /linear/:id/legs: a server x resolution, its DB leg (if any), the real state and the verdict. */
export interface LegStateRow {
  resolutionId: number;
  resolutionName: string;
  desired: boolean;
  leg: { id: number; name?: string; dbStatus: string; lastError: string | null; statusCode: string | null; lastAction?: string | null; lastActionAt?: string | null; held?: boolean; heldReason?: 'user-stop' | 'unwanted' } | null;
  real: LegReal | 'missing' | 'unknown';
  verdict: LegVerdict;
  /** Name the row acts on (DB leg name, or the expected name). */
  name?: string;
  expectedName?: string;
  expectedNameTaken?: boolean;
  /** The leg keeps an old name the user accepted ("Accept the name"). */
  nameAccepted?: boolean;
  /** Scoped actions that would act on this row now (server-side rules): only these buttons are shown. */
  actions?: LegActionName[];
}

export type LegActionName = 'create' | 'start' | 'stop' | 'restart' | 'delete' | 'forget' | 'adopt' | 'repair' | 'rename' | 'accept';

export interface LegActionScope {
  servers: number[];
  resolutions?: number[];
  legIds?: number[];
  /** Streams without a DB row, by server ref (id / uuid), optionally with the name seen in the preview. */
  refs?: Array<string | { ref: string; name: string }>;
}

/** One row of a dry-run plan: would the action touch it (act), or why not (skipped). */
export interface LegPlanItem {
  serverId: number;
  serverName: string;
  serverType: string;
  resolutionId: number;
  resolutionName: string;
  legId?: number;
  legName: string;
  newName?: string;
  ref?: string;
  untagged?: boolean;
  verdict: string;
  realStatus: string;
  act: boolean;
  op?: string;
  skipped?: string;
  alreadyDone?: boolean;
}

export interface LegActionResult {
  serverId: number;
  serverName: string;
  resolutionId: number;
  resolutionName: string;
  legId?: number;
  legName: string;
  ref?: string;
  ok: boolean;
  alreadyDone?: boolean;
  skipped?: string;
  error?: string;
}

export interface LegStateServer {
  id: number;
  name: string;
  type: 'xaccel' | 'ssp' | 'ffstream' | 'unknown';
  reachable: boolean;
  stateAgeMs: number | null;
  complete: boolean;
  fromCache: boolean;
  error?: string;
  rows: LegStateRow[];
}

/** Bulk config push: only updates the server accepted count; refusals listed per leg; unwanted legs skipped. */
export interface PushConfigResult {
  updated: number;
  failed?: number;
  skipped?: number;
  total: number;
  failures?: Array<{ channelName: string; serverId: number; error: string }>;
}

export const linearService = {
  async list(): Promise<LinearChannel[]> {
    const response = await api.get<ApiResponse<LinearChannel[]>>('/linear');
    return response.data.data;
  },

  async get(id: number): Promise<LinearChannel> {
    const response = await api.get<ApiResponse<LinearChannel>>(`/linear/${id}`);
    return response.data.data;
  },

  async create(data: CreateLinearChannelRequest): Promise<LinearChannel> {
    const response = await api.post<ApiResponse<LinearChannel>>('/linear', data);
    return response.data.data;
  },

  async update(id: number, data: UpdateLinearChannelRequest, opts: { timeoutMs?: number } = {}): Promise<LinearChannel> {
    const response = await api.put<ApiResponse<LinearChannel>>(`/linear/${id}`, data, opts.timeoutMs ? { timeout: opts.timeoutMs } : undefined);
    return response.data.data;
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/linear/${id}`, { params: { confirm: true } });
  },

  /** Drop the channel from the CMS only: its streams stay on the servers (explicit, confirmed). */
  async removeFromCms(id: number): Promise<{ removed: boolean; legsLeft: Array<{ channelName: string; server: string }> }> {
    const response = await api.post<ApiResponse<{ removed: boolean; legsLeft: Array<{ channelName: string; server: string }> }>>(`/linear/${id}/remove-from-cms`, { confirm: true });
    return response.data.data;
  },

  async start(id: number): Promise<LinearChannel & { skippedHeld?: number }> {
    const response = await api.post<ApiResponse<LinearChannel> & { skippedHeld?: number }>(`/linear/${id}/start`);
    return { ...response.data.data, ...(response.data.skippedHeld ? { skippedHeld: response.data.skippedHeld } : {}) };
  },

  async stop(id: number): Promise<LinearChannel> {
    const response = await api.post<ApiResponse<LinearChannel>>(`/linear/${id}/stop`, { confirm: true });
    return response.data.data;
  },

  async restart(id: number): Promise<LinearChannel> {
    const response = await api.post<ApiResponse<LinearChannel>>(`/linear/${id}/restart`, { confirm: true });
    return response.data.data;
  },

  async refreshKeys(id: number): Promise<LinearChannel> {
    const response = await api.post<ApiResponse<LinearChannel>>(`/linear/${id}/refresh-keys`);
    return response.data.data;
  },

  // Wizard
  async wizard(): Promise<WizardChannel[]> {
    const response = await api.get<ApiResponse<WizardChannel[]>>('/linear/wizard');
    return response.data.data;
  },

  async importWizard(channels: Array<{ dazn_asset_id: string; name: string; subtitle?: string; image_url?: string }>, prefix?: string): Promise<{ data: LinearChannel[]; message: string }> {
    const response = await api.post<ApiResponse<LinearChannel[]> & { message: string }>('/linear/import-wizard', { channels, prefix });
    return { data: response.data.data, message: response.data.message || '' };
  },

  async bulkConfigure(request: BulkConfigureRequest): Promise<BulkConfigureResponse> {
    const response = await api.post<ApiResponse<BulkConfigureResponse>>('/linear/bulk-configure', request, { timeout: 600_000 });
    return response.data.data;
  },

  /** Wizard preview (dry run): nothing is saved or created. */
  async bulkPlan(request: BulkConfigureRequest): Promise<BulkPlanServer[]> {
    const response = await api.post<ApiResponse<{ plan: { servers: BulkPlanServer[] } }>>('/linear/bulk-configure', { ...request, dryRun: true }, { timeout: 60_000 });
    return response.data.data.plan.servers;
  },

  /** Retry: create the missing legs of these channels on exactly these servers. */
  async bulkCreateLegs(channels: Array<{ id: number; servers: number[] }>): Promise<{ created: number; failed: number; results: BulkServerResult[] }> {
    const response = await api.post<ApiResponse<{ created: number; failed: number; results: BulkServerResult[] }>>('/linear/bulk-create-legs', { channels, confirm: true }, { timeout: 600_000 });
    return response.data.data;
  },

  // Resolutions
  async updateResolutions(id: number, resolutionIds: number[]): Promise<void> {
    await api.put(`/linear/${id}/resolutions`, { resolution_ids: resolutionIds });
  },

  // DRM Keys
  async getKeys(id: number): Promise<{ keys_count: number; is_clear: boolean }> {
    const response = await api.post<ApiResponse<{ keys_count: number; is_clear: boolean }>>(`/linear/${id}/get-keys`);
    return response.data.data;
  },

  // Sub-channels with their real status + one entry per queried server (`servers`: which ones did not answer).
  // Client timeout: a hung request ends in an error state instead of a spinner forever.
  async listXaccelChannels(id: number): Promise<{ channels: LinearXaccelChannel[]; servers: ServerReadEntry[]; orphanedLegs: OrphanedLegs[] }> {
    const response = await api.get<ApiResponse<LinearXaccelChannel[]> & { servers?: ServerReadEntry[]; orphanedLegs?: OrphanedLegs[] }>(`/linear/${id}/xaccel-channels`, { timeout: XACCEL_TAB_REQUEST_TIMEOUT_MS });
    return { channels: response.data.data, servers: response.data.servers || [], orphanedLegs: response.data.orphanedLegs || [] };
  },

  // Read-only per-server state (inventory + verdicts). refresh = fresh read of every server (8 s budget each).
  async listLegs(id: number, refresh = false): Promise<LegStateServer[]> {
    const response = await api.get<ApiResponse<{ channelId: number; servers: LegStateServer[] }>>(`/linear/${id}/legs`, {
      params: refresh ? { refresh: 1 } : undefined,
      timeout: XACCEL_TAB_REQUEST_TIMEOUT_MS,
    });
    return response.data.data.servers;
  },

  // Scoped leg actions (phase 3). plan = dry run (never writes); the others act only on `servers` and need confirm.
  async planLegs(id: number, action: LegActionName, scope: LegActionScope): Promise<LegPlanItem[]> {
    const response = await api.post<ApiResponse<{ action: string; items: LegPlanItem[] }>>(`/linear/${id}/legs/plan`, { action, ...scope }, { timeout: XACCEL_TAB_REQUEST_TIMEOUT_MS });
    return response.data.data.items;
  },

  async runLegAction(id: number, action: LegActionName, scope: LegActionScope): Promise<LegActionResult[]> {
    const response = await api.post<ApiResponse<{ action: string; results: LegActionResult[] }>>(`/linear/${id}/legs/${action}`, { ...scope, confirm: true }, { timeout: LEG_ACTION_REQUEST_TIMEOUT_MS });
    return response.data.data.results;
  },

  // Sync status from Xaccel (update DB with real status). parentStatus null = no server answered, nothing changed.
  async syncStatus(id: number): Promise<{ synced: number; parentStatus: string | null; servers?: ServerReadEntry[] }> {
    const response = await api.post<ApiResponse<{ synced: number; parentStatus: string | null; servers?: ServerReadEntry[] }>>(`/linear/${id}/sync-status`, undefined, { timeout: XACCEL_TAB_REQUEST_TIMEOUT_MS });
    return response.data.data;
  },

  // Sync channels from streaming server (re-create missing DB rows)
  async syncChannels(id: number): Promise<{ created: number; total: number }> {
    const response = await api.post<ApiResponse<{ created: number; total: number }>>(`/linear/${id}/sync-channels`);
    return response.data.data;
  },

  // Full restart with keys
  async restartWithKeys(id: number, serverId?: number): Promise<LinearChannel> {
    const qs = serverId !== undefined ? `?serverId=${serverId}` : '';
    const response = await api.post<ApiResponse<LinearChannel>>(`/linear/${id}/restart-with-keys${qs}`, { confirm: true });
    return response.data.data;
  },

  // Bulk operations
  async startAll(): Promise<{ started: number; skippedHeld?: number }> {
    const response = await api.post<ApiResponse<{ started: number; skippedHeld?: number }>>('/linear/start-all', { confirm: true });
    return response.data.data;
  },

  async stopAll(): Promise<{ stopped: number }> {
    const response = await api.post<ApiResponse<{ stopped: number }>>('/linear/stop-all', { confirm: true });
    return response.data.data;
  },

  /** `confirmCount` = the number of running channels the user typed (the server checks it). */
  async restartAll(confirmCount: number): Promise<{ restarted: number; skippedHeld?: number }> {
    const response = await api.post<ApiResponse<{ restarted: number; skippedHeld?: number }>>('/linear/restart-all', { confirm: true, confirmCount });
    return response.data.data;
  },

  async pushVideoMap(id: number): Promise<PushConfigResult> {
    const response = await api.post<ApiResponse<PushConfigResult>>(`/linear/${id}/push-video-map`);
    return response.data.data;
  },

  async pushConfig(id: number): Promise<PushConfigResult> {
    const response = await api.post<ApiResponse<PushConfigResult>>(`/linear/${id}/push-config`);
    return response.data.data;
  },

  async deleteAll(): Promise<{ deleted: number; kept?: Array<{ id: number; name: string; errors: string[] }> }> {
    const response = await api.delete<ApiResponse<{ deleted: number; kept?: Array<{ id: number; name: string; errors: string[] }> }>>('/linear/delete-all', { params: { confirm: true } });
    return response.data.data;
  },
};
